import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ButtonWithLoader, Col, Row, Wizard } from '@valid-eval/shared-react-components';

import GenericModal from 'components/GenericModal';
import Table from 'components/Tables/Table';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';

import { useFetchEventUsers, useRemoveUser } from 'data/features/eventsHooks';
import { EventUser } from 'data/features/eventsTypes';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventStepComponentProps } from '.';
import { AddUser } from './components/AddUser';
import EditEventStepForm from './components/EditEventStepForm';
import { columns } from './UsersTableDefinition';

function NavigatorsStepComponent({
  event,
  label,
  subLabel,
  step,
  nextStep,
}: EventStepComponentProps) {
  const { t } = useTranslation();
  const { navigators, organizers } = useFetchEventUsers(event.id);
  const { removeUserFromEvent, isLoadingUsers } = useRemoveUser();
  const [userToDelete, setUserToDelete] = useState<EventUser | null>(null);
  const [showDeleteModal, openDeleteModal, closeDeleteModal] = useBooleanFlag(false);

  const userLabel = step.id === 'organizers' ? 'Organizer' : 'Navigator';

  const handleConfirmDelete = () => {
    if (userToDelete) {
      removeUserFromEvent(event.id, userToDelete.id);
      setUserToDelete(null);
    }
    closeDeleteModal();
  };

  const handleCancelDelete = () => {
    setUserToDelete(null);
    closeDeleteModal();
  };

  const deleteUser = (user: EventUser) => {
    setUserToDelete(user);
    openDeleteModal();
  };

  const tableModel = useReactTable<any>({
    data: step.id === 'organizers' ? organizers : navigators || [],
    columns,
    meta: { deleteUser },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <EditEventStepForm label={label} subLabel={subLabel}>
      <Row className="mt-3 mb-2">
        <Col xs={12} className="d-flex justify-content-end">
          <AddUser event={event} userType={step.id} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <Table tableModel={tableModel} loading={isLoadingUsers} />
        </Col>
      </Row>
      <Wizard.Footer>
        <div className="d-flex justify-content-end align-items-center">
          <ButtonWithLoader variant="primary" onClick={nextStep} loading={isLoadingUsers}>
            {t('event.form.next')}
          </ButtonWithLoader>
        </div>
      </Wizard.Footer>

      <GenericModal
        name={`confirm-delete-${step.id}`}
        show={showDeleteModal}
        size="md"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('event.form.confirm_delete_user_title', { userType: userLabel })}
        body={
          <p>
            {t('event.form.confirm_delete_user_message', {
              userType: userLabel,
              userName: `${userToDelete?.firstName} ${userToDelete?.lastName}`,
            })}
          </p>
        }
        confirmButton={t('common.confirm')}
        cancelButton={t('common.cancel')}
        isButtonDisabled={isLoadingUsers}
        isCancelButtonDisabled={isLoadingUsers}
      />
    </EditEventStepForm>
  );
}

export default NavigatorsStepComponent;
